/**
 * DecommissionRadarPanel (Iter 36.f) — "referenced but dead" files.
 *
 * Pairs with the iter 36.e churn panel as the inverse signal:
 *   churn panel  → "files the journal proves get touched"
 *   radar panel  → "files the catalog claims are used but the journal
 *                   says no one writes to"
 *
 * The radar is gated by `has_journal_window` — without a DSPJRN slice the
 * comparison is meaningless (every referenced file would falsely show up).
 *
 * The panel also auto-hides silently when journal data IS present but
 * there are zero candidates (every referenced file is alive — a healthy
 * outcome). Empty-state with explanatory copy renders only when the
 * consultant has imported journal data but is still missing a window AND
 * the workspace has program references to compare against.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArchiveBoxIcon as ArchiveBox,
  CircleNotchIcon as Spinner,
  WarningIcon as Warning,
  CheckCircleIcon as CheckCircle,
  ArrowRightIcon as ArrowRight,
  ShieldWarningIcon as ShieldWarning,
} from "@phosphor-icons/react";
import { getDecommissionRadar } from "@/lib/api";

function fmtCount(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString();
}

function fmtIsoDate(iso) {
  if (!iso) return "—";
  return String(iso).slice(0, 10);
}

export default function DecommissionRadarPanel({ workspaceId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getDecommissionRadar(workspaceId, { limit: 50 })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workspaceId]);

  if (loading) {
    return (
      <div className="ml-card p-4" data-testid="decommission-radar-loading">
        <div className="flex items-center gap-2 text-sm text-[var(--ml-text-secondary)]">
          <Spinner size={14} className="animate-spin" />
          Loading decommission radar…
        </div>
      </div>
    );
  }
  if (!data) return null;

  // Silent hide: journal window exists AND zero dead files = nothing to say.
  if (data.has_journal_window && data.candidates.length === 0) return null;

  // Empty state: workspace has program refs but no journal window yet →
  // surface a CTA to import a DSPJRN slice.
  if (!data.has_journal_window) {
    return (
      <div className="ml-card p-4" data-testid="decommission-radar-empty">
        <div className="flex items-start gap-3">
          <ShieldWarning size={20} weight="duotone" color="#a56600" className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
              Decommission radar
            </div>
            <div className="text-base font-semibold mt-1">No journal window yet</div>
            <div className="text-sm text-[var(--ml-text-secondary)] mt-1 max-w-2xl">
              Import a DSPJRN slice (and ideally DSPJRNRCV) for this workspace to
              surface files the catalog claims are referenced but the journal
              proves no one writes to — sharp deletion candidates for the
              modernization roadmap.
            </div>
            <Link
              to={`/workspaces/${workspaceId}/metadata`}
              data-testid="decommission-radar-import-link"
              className="mt-2 inline-flex items-center gap-1 ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline"
            >
              Go to imports
              <ArrowRight size={11} weight="bold" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const t = data.totals;

  return (
    <div className="ml-card p-4 space-y-4" data-testid="decommission-radar-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
            Catalog says &ldquo;used&rdquo; · Journal says &ldquo;dead&rdquo;
          </div>
          <div className="text-base font-semibold mt-1 flex items-center gap-1.5">
            <ArchiveBox size={16} weight="duotone" color="#a56600" />
            Decommission radar &middot; referenced but never written
          </div>
          <div className="text-xs text-[var(--ml-text-secondary)] mt-1">
            Window observed: {fmtIsoDate(data.window_start)} → {fmtIsoDate(data.window_end)}
            {" · "}
            <span data-testid="decommission-radar-files-with-writes">
              {fmtCount(t.files_with_writes)} files saw writes
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
            Candidates
          </div>
          <div
            className="text-2xl font-semibold tabular-nums text-[#a56600]"
            data-testid="decommission-radar-candidate-count"
          >
            {fmtCount(t.candidate_count)}
          </div>
        </div>
      </div>

      {t.candidate_count >= 1 && (
        <div
          className="text-xs text-[var(--ml-text-secondary)] flex items-start gap-1.5 bg-[#fcf4d6] border-l-2 border-[#a56600] px-3 py-2"
          data-testid="decommission-radar-callout"
        >
          <Warning size={12} weight="bold" className="flex-shrink-0 mt-[2px]" color="#a56600" />
          <span>
            <strong className="text-[var(--ml-text)]">
              ~{fmtCount(t.total_records)} rows
            </strong>{" "}
            could be archived if these files are confirmed dead. Validate by
            cross-checking with business owners before any DROP.
          </span>
        </div>
      )}

      <div className="overflow-x-auto" data-testid="decommission-radar-table-wrap">
        <table className="w-full text-sm" data-testid="decommission-radar-table">
          <thead>
            <tr className="border-b border-[var(--ml-border)] bg-[var(--ml-surface)]">
              <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">File</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Programs</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Refs</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Rows</th>
              <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Journaled</th>
              <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Description</th>
            </tr>
          </thead>
          <tbody>
            {data.candidates.map((row) => (
              <tr
                key={`${row.library_name}/${row.file_name}`}
                className="border-b border-[var(--ml-border)] last:border-b-0"
                data-testid={`decommission-row-${row.file_name}`}
              >
                <td className="px-2 py-1.5 font-mono text-xs">
                  <span className="text-[var(--ml-text-secondary)]">{row.library_name}/</span>
                  {row.file_name}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{fmtCount(row.ref_program_count)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-[var(--ml-text-secondary)]">{fmtCount(row.ref_count)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{fmtCount(row.record_count)}</td>
                <td className="px-2 py-1.5">
                  {row.journaled ? (
                    <span className="inline-flex items-center gap-1 ml-mono text-[10px] uppercase tracking-widest text-[#0e6027]">
                      <CheckCircle size={11} weight="fill" /> yes
                    </span>
                  ) : (
                    <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
                      no
                    </span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-xs text-[var(--ml-text-secondary)] truncate max-w-xs">
                  {row.text_description || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
